import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';

/**
 * Componente de select pesquisável para filtros simples (strings)
 * Props:
 * - options: string[] - Lista de opções
 * - value: string - Valor selecionado
 * - onChange: fn(string) - Callback quando seleciona
 * - placeholder: string - Placeholder do input
 * - disabled: bool - Desabilitado
 * - allOptionLabel: string - Label para opção "todos/todas" (ex: "Todas as cidades")
 * - allOptionValue: string - Valor para opção "todos/todas" (ex: "todas")
 */
export default function SearchableFilterSelect({
  options = [],
  value = '',
  onChange,
  placeholder = 'Buscar...',
  disabled = false,
  allOptionLabel = null,
  allOptionValue = null,
  icon: Icon = null,
  className = '',
  inputId,
  ariaLabel,
  dropdownClassName = '',
  compact = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtered, setFiltered] = useState([]);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Filtrar opções baseado no termo de busca
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFiltered(options);
      return;
    }

    const term = searchTerm.toLowerCase().trim();
    const filteredOptions = options.filter(opt => 
      opt.toLowerCase().includes(term)
    );
    setFiltered(filteredOptions);
  }, [searchTerm, options]);

  // Atualizar termo de busca quando valor muda
  useEffect(() => {
    if (value && value !== allOptionValue) {
      setSearchTerm(value);
    } else {
      setSearchTerm('');
    }
  }, [value, allOptionValue]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (selectedValue) => {
    onChange?.(selectedValue);
    setIsOpen(false);
    // Resetar termo de busca após seleção
    setSearchTerm('');
  };

  const handleClear = () => {
    if (allOptionValue) {
      onChange?.(allOptionValue);
    } else {
      onChange?.('');
    }
    setSearchTerm('');
    inputRef.current?.focus();
  };

  const displayValue = value && value !== allOptionValue ? value : '';
  const hasValue = value && value !== allOptionValue;
  // Quando não há valor, mostrar o placeholder ou o allOptionLabel
  const displayPlaceholder = !hasValue && allOptionLabel ? allOptionLabel : placeholder;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)] h-4 w-4" />
        )}
        <input
          id={inputId}
          aria-label={ariaLabel}
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : displayValue}
          onChange={(e) => { 
            setSearchTerm(e.target.value); 
            setIsOpen(true); 
          }}
          onFocus={() => {
            setIsOpen(true);
            // Quando focar, limpar o termo de busca para mostrar todas as opções
            setSearchTerm('');
          }}
          placeholder={!hasValue ? displayPlaceholder : placeholder}
          disabled={disabled}
          className={`w-full ${Icon ? 'pl-10' : 'pl-3'} pr-20 ${compact ? 'py-2 text-sm' : 'py-2.5 text-sm'} border border-[color:var(--border)] rounded-md shadow-sm focus:outline-none focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)] text-[color:var(--text-primary)] placeholder-[color:var(--text-muted)] ${
            disabled ? 'bg-[color:var(--surface-muted)] cursor-not-allowed' : 'bg-[color:var(--surface)]'
          }`}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center space-x-1">
          {hasValue && (
            <button 
              type="button" 
              onClick={handleClear} 
              className="p-1 hover:bg-[color:var(--surface-muted)] rounded-full transition-colors"
              tabIndex={-1}
            >
              <X className="h-4 w-4 text-[color:var(--text-muted)]" />
            </button>
          )}
          <button 
            type="button" 
            onClick={() => setIsOpen(!isOpen)} 
            className="p-1 hover:bg-[color:var(--surface-muted)] rounded-full transition-colors"
            tabIndex={-1}
          >
            <ChevronDown className={`h-4 w-4 text-[color:var(--text-muted)] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>
      {isOpen && (
        <div className={`absolute z-50 w-full mt-1 bg-[color:var(--surface)] border border-[color:var(--border)] rounded-md shadow-lg max-h-60 overflow-y-auto ${dropdownClassName}`}>
          {allOptionLabel && allOptionValue && (
            <button
              type="button"
              onClick={() => handleSelect(allOptionValue)}
              className={`w-full px-3 py-2 text-left hover:bg-[color:var(--surface-muted)] transition-colors ${
                value === allOptionValue ? 'bg-[color:var(--surface-muted)] font-medium' : ''
              }`}
            >
              {allOptionLabel}
            </button>
          )}
          {filtered.length > 0 ? (
            filtered.map((opt, index) => (
              <button
                key={`${opt}-${index}`}
                type="button"
                onClick={() => handleSelect(opt)}
                className={`w-full px-3 py-2 text-left hover:bg-[color:var(--surface-muted)] transition-colors ${
                  value === opt ? 'bg-[color:var(--surface-muted)] font-medium' : ''
                }`}
              >
                {opt}
              </button>
            ))
          ) : (
            <div className="p-3 text-center text-[color:var(--text-muted)] text-sm">Sem resultados</div>
          )}
        </div>
      )}
    </div>
  );
}
